/**
 * The previous and next day, as the page header's period, within the run's
 * horizon and never beyond it: the API answers a day outside the horizon with
 * DAY_OUTSIDE_HORIZON, so a link there would be a link to a refusal. The
 * selected trip is not carried over, because a route id belongs to one day.
 *
 * Each link shows the short date and is named in full ("Previous day, 30
 * Sept 2026"); the visible text is part of the name (WCAG 2.5.3). The
 * header draws the arrows.
 */
import type { PagePeriod } from "@depicomnlbv/platform-ui";
import { Link } from "react-router-dom";

import type { Run } from "../api";
import { addDays, formatShortDate } from "../time";

const dayNumber = (from: string, to: string) =>
  Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;

export function dayPeriod(run: Run, date: string): PagePeriod {
  const previous = addDays(date, -1);
  const next = addDays(date, 1);
  const base = `/runs/${run.run_id}/days`;
  return {
    label: "Days of this run",
    current: `Day ${dayNumber(run.horizon_start, date)} of ${dayNumber(run.horizon_start, run.horizon_end)}`,
    previous:
      previous >= run.horizon_start ? (
        <Link aria-label={`Previous day, ${formatShortDate(previous)}`} rel="prev" to={`${base}/${previous}`}>
          {formatShortDate(previous)}
        </Link>
      ) : undefined,
    next:
      next <= run.horizon_end ? (
        <Link aria-label={`Next day, ${formatShortDate(next)}`} rel="next" to={`${base}/${next}`}>
          {formatShortDate(next)}
        </Link>
      ) : undefined,
  };
}
